import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { TableModule } from 'primeng/table';
import { CalendarModule } from 'primeng/calendar';
import { SidebarModule } from 'primeng/sidebar';
import { DialogModule } from 'primeng/dialog';
import { PropertyService } from '../properties/service/property.service';
import { MessageService } from 'primeng/api';
import { TemplateService } from './templates.service';
import { SendToType } from '../../enums/send-to-type.enum';
import { CreateTemplateComponent } from '../create-template/create-template.component';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';

@Component({
  selector: 'app-templates',
  standalone: true,
  imports: [
    TableModule,
    CommonModule,
    DropdownModule,
    InputTextModule,
    ButtonModule,
    FormsModule,
    CalendarModule,
    SidebarModule,
    DialogModule,
    ReactiveFormsModule,
    CreateTemplateComponent,
    DragDropModule
  ],
  templateUrl: './templates.component.html',
  styleUrls: ['./templates.component.scss'],
  providers: [MessageService]
})

export class TemplatesComponent implements OnInit {
  calendlyLink: string = '';
  showLinkDialog: boolean = false;
  templates: any[] = [];
  propertyId = "6724ec85459e3cdf8db55149";
  totalRecords: number = 0;
  currentPage: number = 0;
  itemsPerPage = 10;

  filters: any = {
    plan_name: null,
    user_name: null,
    status: null,
    appointmentDate: null,
    search: ''
  };
  sorting: any = {
    field: '',
    order: 1
  };
  agents: any[] = [];
  statuses: any[] = [];
  displaySidebar: boolean = false;
  showCancelDialog: boolean = false;
  showDeleteDialog: boolean = false;
  templateToDelete: any = null;
  propertyDetails: any = {};
  userInfo: any = null;
  timeZone: string = '';
  position: 'center' | 'right' = 'center';
  visible: boolean = false;
  selectedTemplate: any | null = null;
  SendToType = SendToType;
  templateName: string | null = null;
  routeName: string | null = null;
  templatesOf: any | null = null;
  private routeToSendToTypeMap: { [key: string]: SendToType } = {
    lead: SendToType.LEAD,
    resident: SendToType.RESIDENT,
    both: SendToType.BOTH,
    'external-lead': SendToType.EXTERNAL_LEAD,
  };
  constructor(
    private fb: FormBuilder,
    private propertyService: PropertyService,
    private messageService: MessageService,
    private templateService: TemplateService,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params: ParamMap) => {
      this.routeName = params.get('type') as SendToType;
      this.templateName = this.capitalize(this.routeName);
      this.templatesOf = this.routeToSendToTypeMap[this.routeName ?? ''];
      if (this.templatesOf) {
        this.loadTemplates(this.templatesOf, {
          first: this.currentPage * this.itemsPerPage,
          rows: this.itemsPerPage,
        });
      }
    });
    this.propertyService.getUserInfo().subscribe(user => {
      this.userInfo = user;
      this.propertyId = user.property_id
    });
  }

  private readonly routeNameLabels: { [key: string]: string } = {
    'external-lead': 'New Lead - No Showing Scheduled',
  };

  capitalize(str: string): string {
    return this.routeNameLabels[str] ?? (str.charAt(0).toUpperCase() + str.slice(1));
  }

  loadTemplates(type: SendToType, params?: any) {
    console.log("Loading templates for type:", type, "with params:", params)
    const first = params?.first || 0;
    const rows = params?.rows || 10;
    const sortField = params?.sortField || 'sortId';
    const sortOrder = params?.sortOrder || '1';
    const page = first / rows + 1;
    const limit = rows;

    const queryParams = {
      page,
      limit,
      sortField: sortField,
      sortOrder: sortOrder,
    };
    console.log("Query Params:", queryParams);
    this.templateService.getTemplatesByType(type, queryParams).subscribe({
      next: (res: any) => {
        this.templates = res.data;
        this.totalRecords = res.total;
        this.currentPage = page - 1;
      },
      error: (err) => {
        console.error(`Failed to load ${type} templates`, err);
      }
    });
  }

  onSorting(event: any): void {
    this.sorting.field = event.field;
    this.sorting.order = event.order;
    this.loadTemplates(this.templatesOf, {
      first: this.currentPage * this.itemsPerPage,
      rows: this.itemsPerPage,
    });
  }

  onDrop(event: CdkDragDrop<any[]>) {
    if (event.previousIndex === event.currentIndex) return;

    moveItemInArray(this.templates, event.previousIndex, event.currentIndex);

    // Update sortId based on the new order
    this.templates.forEach((template, index) => {
      template.sortId = index + 1; // 1-based index
    });

    // Send updated sortIds to backend (you can batch or do one by one)
    //this.updateSortOrderInBackend(this.templates);
  }

  openSidebar(): void {
    this.displaySidebar = true;
  }

  cancelAddTemplates(): void {
    this.showCancelDialog = true;
  }

  confirmCancelAdd(): void {
    this.displaySidebar = false;
    this.showCancelDialog = false;
  }

  declineCancel(): void {
    this.showCancelDialog = false;
  }

  confirmDeleteTemplate(template: any): void {
    this.templateToDelete = template;
    this.showDeleteDialog = true;
  }

  deleteTemplate(): void {
    console.log('this.templateToDelete', this.templateToDelete)
    if (!this.templateToDelete) return;
    this.templateService.deleteTemplate(this.templateToDelete._id).subscribe({
      next: (res: any) => {
        this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Template deleted successfully' });
        this.showDeleteDialog = false;
      },
      error: (err: any) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not delete template' });
        this.showDeleteDialog = false;
      }
    });
  }

  cancelDelete(): void {
    this.showDeleteDialog = false;
  }

  onDialogClose(): void {
    this.visible = false;
    this.selectedTemplate = null;
    this.loadTemplates(this.templatesOf, {
      first: this.currentPage * this.itemsPerPage,
      rows: this.itemsPerPage,
    });
  }

  showDialogForEdit(position: 'right', template: any): void {
    this.position = position;
    this.selectedTemplate = template;
    this.visible = true;
  }

  showDialogForCreate(position: 'right'): void {
    this.position = position;
    this.selectedTemplate = null;
    this.visible = true;
  }

  generateSendTimeMessage(options: {
    entity?: string;
    eventType?: string;
    sendTimeCondition: 'immediate' | 'before' | 'after' | 'no_response';
    sendTime: number;
    timeUnit?: 'hour';
  }): string {
    const {
      entity,
      eventType,
      sendTimeCondition,
      sendTime,
      timeUnit = 'hour',
    } = options;
    const pluralize = (val: number, unit: string) => {
      if (val === 1) return `One ${unit}`;
      return `${val} ${unit}${val > 1 ? 's' : ''}`;
    };
    if (sendTimeCondition === 'immediate' || sendTime === 0) {
      if (entity && eventType) return `${entity} is ${eventType}`;
      return eventType || 'Immediate';
    }
    if (sendTimeCondition === 'no_response') {
      const timeMsg = pluralize(sendTime, timeUnit);
      return `No Response — ${timeMsg} after lead is pulled`;
    }
    const timeMsg = pluralize(sendTime, timeUnit);
    return `${timeMsg} ${sendTimeCondition} tour's schedule time`;
  }
}